import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Badge } from '@/components/ui/daisy';
import { handleUserFullName } from '@/functions/handleUserFullName';
import { OccurrenceRecord } from '@/utils/assessments/klassPedagogicalSheetRecordTypes';
import RecordCardMeta from './RecordCardMeta';

type OccurrenceCardProps = {
  record: OccurrenceRecord;
  canManage: boolean;
  onEdit: (record: OccurrenceRecord) => void;
  onDelete: (record: OccurrenceRecord) => Promise<void>;
  showStudent?: boolean;
};

export default function OccurrenceCard({
  record,
  canManage,
  onEdit,
  onDelete,
  showStudent = true,
}: OccurrenceCardProps) {
  const { author, student, occurrenceAssessment } = record.assessment;
  const studentName = handleUserFullName(student);

  return (
    <CourseAdminCard variant="card" className="bg-primary-content">
      <CourseAdminCard.Header>
        <CourseAdminCard.Leading>
          <RecordCardMeta
            datetime={record.datetime}
            authorName={handleUserFullName(author)}
          />
          <CourseAdminCard.LeadingIcon>
            <ExclamationCircleIcon className="size-6 text-base-content" />
          </CourseAdminCard.LeadingIcon>
          <CourseAdminCard.Title className="whitespace-normal break-words font-normal">
            {occurrenceAssessment?.description}
          </CourseAdminCard.Title>
          <ConditionalRenderer condition={showStudent && !!studentName}>
            <CourseAdminCard.Badges className="shrink-0 gap-2">
              <Badge size="sm" variant="default" color="primary">
                {studentName}
              </Badge>
            </CourseAdminCard.Badges>
          </ConditionalRenderer>
        </CourseAdminCard.Leading>

        <ConditionalRenderer condition={canManage}>
          <CourseAdminCard.Actions>
            <CourseAdminCard.EditButton onClick={() => onEdit(record)} />
            <CourseAdminCard.MoreActions>
              <CourseAdminCard.MoreActions.Delete
                confirm
                onClick={() => onDelete(record)}
              />
            </CourseAdminCard.MoreActions>
          </CourseAdminCard.Actions>
        </ConditionalRenderer>
      </CourseAdminCard.Header>
    </CourseAdminCard>
  );
}
